import React, {
    FC, ReactElement, ReactNode, useCallback, useEffect, useState,
} from "react";
import {Nullable} from "@data-force/app";
import {Badge, Button} from "antd";
import {ArrowRightOutlined} from "@ant-design/icons";
import {Translate, TranslateFunction} from "react-localize-redux";
import {useDispatch, useSelector} from "react-redux";
import {Dispatch} from "redux";
import {
    AutoSizer, CellMeasurer, CellMeasurerCache, List, ListRowProps, ScrollParams, Size,
} from "react-virtualized";
import classNames from "classnames";
import Spinner from "../../common/Spinner/Spinner";
import SearchField from "../../common/SearchField/SearchField";
import {Task, TaskPhase} from "../../../interfaces/store/actions/payload/task";
import {JobEntity, PhaseConfigNormalized} from "../../../interfaces/store/actions/payload/job";
import {getOpeningUrl, isExternalProject} from "../../../utils/common";
import {getTaskList} from "../../../store/actions/task";
import {openExternalJob} from "../../../store/actions/job";
import {GetTaskListAction} from "../../../interfaces/store/actions/task";
import {OpenExternalJobAction} from "../../../interfaces/store/actions/job";
import {Cursor} from "../../../interfaces/store/cursor";
import {Editors, TaskPhaseStatusColors} from "../../../interfaces/common";
import {selectTasks, selectTasksCursor} from "../../../store/selectors/task";
import {selectTranslate} from "../../../store/selectors/translate";
import {selectEditors} from "../../../store/selectors/serviceProperties";
import {selectPhaseConfigList, selectSelectedJob} from "../../../store/selectors/job";
import {selectExternalUrlLoading, selectLoading} from "../../../store/selectors/mask";
import {TaskStatusMap} from "../../../utils/constants";
import "./TaskList.scss";
import {JobTag, NoResultType, PhaseStatus} from "../../../enums/enums";
import ContentEmpty from "../../common/ContentEmpty/ContentEmpty";
import {TabManager} from "../../../utils/TabManager";

const TaskList: FC = (): ReactElement => {
    const translationPath = "components.DetailsDrawer.TaskList.TaskList";

    const [searchValue, setSearchValue] = useState<string>("");

    const tasks: Task[] = useSelector(selectTasks);
    const cursor: Nullable<Cursor> = useSelector(selectTasksCursor);
    const translate: TranslateFunction = useSelector(selectTranslate);
    const editors: Editors = useSelector(selectEditors);
    const current: JobEntity = useSelector(selectSelectedJob) || {} as JobEntity;
    const phaseConfigList: PhaseConfigNormalized = useSelector(selectPhaseConfigList);
    const isLoading: boolean = useSelector(selectLoading);
    const isExternalUrlLoading: boolean = useSelector(selectExternalUrlLoading);

    const dispatch: Dispatch<GetTaskListAction | OpenExternalJobAction> = useDispatch();

    const cache: CellMeasurerCache = new CellMeasurerCache({
        defaultHeight: 54,
        minHeight: 54,
        fixedHeight: true,
        minWidth: 1000,
        keyMapper: () => 1,
    });

    useEffect((): void => {
        dispatch(getTaskList());
    }, [dispatch]);

    /**
     * Scroll handler will load more projects to list
     * @param {number} scrollHeight
     * @param {number} clientHeight
     * @param {number} scrollTop
     */
    const onScrollHandler = ({scrollHeight, clientHeight, scrollTop}: ScrollParams): void => {
        if (scrollHeight !== 0
            && ((Math.floor(scrollTop) === (scrollHeight - clientHeight)) || (Math.ceil(scrollTop) === (scrollHeight - clientHeight)))) {
            dispatch(getTaskList({
                cursor,
                name: searchValue,
            }));
        }
    };

    /**
     * Get row of titles for all task list columns
     * @return {ReactNode}
     */
    const getTitleRow = (): ReactNode => (
        <div className="df-task-list-header">
            <div className="df-task-list-status">
                <Translate id={`${translationPath}.statusHeader.text`}/>
            </div>
            <div className="df-task-list-name">
                <Translate id={`${translationPath}.taskNameHeader.text`}/>
            </div>
        </div>
    );

    /**
     * Based on the phase status, we use the "status" key for badge info
     * @param {Nullable<TaskPhase>} phaseStats
     * @return {ReactNode}
     */
    const getTaskItemStatus = (phaseStats: Nullable<TaskPhase>): ReactNode => {
        const {EXPIRED, COMPLETED}: typeof PhaseStatus = PhaseStatus;
        let taskStatus = "ready";
        let taskStatusCmp: React.ReactNode = <></>;
        if (phaseStats) {
            let {status}: TaskPhase = phaseStats;
            if (current.tags.includes(JobTag.EXPIRED) && status !== COMPLETED) {
                taskStatus = EXPIRED;
                status = EXPIRED;
            }
            taskStatus = status.toLowerCase();

            taskStatusCmp = (
                <Badge
                    color={TaskStatusMap[status] as keyof TaskPhaseStatusColors}
                    text={translate(`enums.TaskStatus.${taskStatus}.text`)}
                />
            );
        }

        return taskStatusCmp;
    };

    /**
     * Show this when there is no data for the list
     * @return {ReactElement}
     */
    const noContentRenderer = (): ReactElement => (isLoading ? <></> : <ContentEmpty type={NoResultType.SEARCH_NOT_FOUND}/>);

    /**
     * Search task from task list
     * @param {string} name
     */
    const searchTaskHandler = useCallback((name: string): void => {
        setSearchValue(name);
        dispatch(getTaskList({
            name,
        }));
    }, [dispatch]);

    /**
     * Open appropriate editor for task currently clicked
     * @param {string} id
     * @param {string} payload
     * @param {string} editorUrl
     * @param {boolean} externalProject
     */
    const openEditor = ({id, payload}: Task, editorUrl: string, externalProject: boolean): void => {
        const {projectId, jobId, name}: JobEntity = current;
        let url: Nullable<string>;

        if (externalProject) {
            dispatch(openExternalJob({
                projectId, jobId, phaseName: name, taskId: id,
            }));
        } else {
            url = `${editorUrl}/${payload}/${id}`;
        }

        const tabManager: TabManager = TabManager.getInstance();
        tabManager.openTab(url);
    };

    /**
     * Contributor list cell renderer
     * @param {string} key
     * @param {React.CSSProperties} style
     * @param {React.Component} parent
     * @param {number} index
     * @return {ReactNode}
     */
    const renderRow = ({
        key, style, parent, index,
    }: ListRowProps): ReactNode => {
        const taskData: Task = tasks[index];
        const {
            REJECTED, FAILED, EXPIRED, COMPLETED,
        }: typeof PhaseStatus = PhaseStatus;
        const editorUrl: string = getOpeningUrl(editors, phaseConfigList, current);
        const phaseStats: Nullable<TaskPhase> = taskData.phases.find(({name}: TaskPhase): boolean => name === current.name);
        const externalProject: boolean = isExternalProject(current.projectType);
        let phaseStatus: Nullable<string> = phaseStats?.status;

        // adjust phase status to expired when job tags contain expired and phase is not completed
        if (current.tags?.includes(JobTag.EXPIRED) && phaseStats?.status !== COMPLETED) {
            phaseStatus = EXPIRED;
        }
        const taskNameClasses: string = classNames({
            "df-task-list-name": true,
            "df-rejected-tasks": phaseStatus === REJECTED || phaseStatus === FAILED || phaseStatus === EXPIRED,
        });

        return (
            <CellMeasurer
                cache={cache}
                key={key}
                parent={parent}
            >
                <div style={{...style}} key={key} className="df-task-list">
                    <Button
                        block
                        type="link"
                        disabled={!editorUrl && !externalProject}
                        onClick={(): void => openEditor(taskData, editorUrl, externalProject)}
                    >
                        <div className="df-task-list-status">
                            {getTaskItemStatus(phaseStats)}
                        </div>
                        <div className={taskNameClasses}>
                            {taskData.name}
                        </div>
                        <ArrowRightOutlined aria-hidden/>
                    </Button>
                </div>
            </CellMeasurer>
        );
    };

    return (
        <>
            <SearchField
                onSearch={searchTaskHandler}
                inputProps={{
                    placeholder: translate(`${translationPath}.searchTasks.text`) as string,
                    "aria-label": translate(`${translationPath}.searchTasks.text`) as string,
                }}
            />
            {getTitleRow()}
            <div className="df-task-list-container">
                {(isLoading || isExternalUrlLoading) && (<Spinner/>)}
                <AutoSizer>
                    {({ width, height }: Size): ReactNode => (
                        <List
                            height={height}
                            width={width}
                            rowRenderer={renderRow}
                            noRowsRenderer={noContentRenderer}
                            deferredMeasurementCache={cache}
                            rowCount={tasks.length}
                            overscanRowCount={10}
                            rowHeight={54}
                            onScroll={onScrollHandler}
                            aria-label={translate(`${translationPath}.listOfTasks.text`) as string}
                        />
                    )}
                </AutoSizer>
            </div>
        </>
    );
};

export default TaskList;
